import type { ApiResponse, PageResult } from '@wanjianshi/shared-contracts';
import { ApiError } from '@wanjianshi/shared-http';
import { useCallback, useEffect, useRef, useState } from 'react';
import { adminApi, isAdminUnauthorizedError } from '@/services/api/client';

function errorMessage(reason: unknown): string {
  return reason instanceof Error ? reason.message : '数据加载失败';
}

export function useAdminRecord<T>(endpoint: string | null) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const load = useCallback(async () => {
    abortRef.current?.abort();
    if (!endpoint) {
      setData(null);
      setLoading(false);
      setError(null);
      return;
    }
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setError(null);
    try {
      const response = await adminApi.get<ApiResponse<T>>(endpoint, { signal: controller.signal });
      setData(response.data);
    } catch (reason) {
      if (reason instanceof ApiError && reason.code === 'REQUEST_ABORTED') return;
      if (isAdminUnauthorizedError(reason)) return;
      setError(errorMessage(reason));
    } finally {
      if (abortRef.current === controller) {
        abortRef.current = null;
        setLoading(false);
      }
    }
  }, [endpoint]);

  useEffect(() => {
    void load();
    return () => abortRef.current?.abort();
  }, [load]);

  return { data, loading, error, reload: load };
}

export function useAdminPage<T>(endpoint: string | null, initialPageSize = 20) {
  const [items, setItems] = useState<T[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const load = useCallback(async () => {
    abortRef.current?.abort();
    if (!endpoint) {
      setItems([]);
      setTotal(0);
      setLoading(false);
      setError(null);
      return;
    }
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setError(null);
    try {
      const response = await adminApi.get<ApiResponse<PageResult<T>>>(endpoint, {
        params: { page, page_size: pageSize },
        signal: controller.signal,
      });
      setItems(response.data.items ?? []);
      setTotal(response.data.total ?? 0);
    } catch (reason) {
      if (reason instanceof ApiError && reason.code === 'REQUEST_ABORTED') return;
      if (isAdminUnauthorizedError(reason)) return;
      setError(errorMessage(reason));
    } finally {
      if (abortRef.current === controller) {
        abortRef.current = null;
        setLoading(false);
      }
    }
  }, [endpoint, page, pageSize]);

  useEffect(() => {
    setPage(1);
  }, [endpoint]);

  useEffect(() => {
    void load();
    return () => abortRef.current?.abort();
  }, [load]);

  return {
    items,
    total,
    page,
    pageSize,
    loading,
    error,
    reload: load,
    setPage,
    setPageSize,
  };
}
